import React, { useMemo } from 'react';

import { integerToCurrency } from 'loot-core/src/shared/util';
import { type CategoryGroupEntity } from 'loot-core/types/models';

import { type CSSProperties, styles, theme } from '../../style';
import { Text } from '../common/Text';
import { View } from '../common/View';
import { useFormat } from '../spreadsheet/useFormat';

import CategoryHeaderCell from './CategoryHeaderCell';
import { type ITableTotals, type VisibleTotals } from './types';
import { VIEW_MODE } from './util';

const headerLabelStyle: CSSProperties = {
  textAlign: 'right',
  minWidth: '100px',
  padding: '0 4px',
};

type IExpenseHeaderProps = {
  year: number;
  categoryGroups: CategoryGroupEntity[];
  groupTotals: { [groupId: string]: ITableTotals };
  visibleTotals: VisibleTotals;
  months: { index: number; month: string }[];
  toggleHiddenCategories: () => void;
  expandAllCategories: () => void;
  collapseAllCategories: () => void;
  viewMode: VIEW_MODE;
};

export default function TableHeader({
  year,
  categoryGroups,
  groupTotals,
  visibleTotals,
  months,
  viewMode,
  toggleHiddenCategories,
  expandAllCategories,
  collapseAllCategories,
}: IExpenseHeaderProps) {
  const format = useFormat();

  const totals: ITableTotals = useMemo(() => {
    let amount = 0;
    let monthly = 0;
    let yearly = 0;

    for (const groupId of Object.keys(groupTotals)) {
      const specificGroupTotals = groupTotals[groupId];
      const group = categoryGroups.find(o => o.id === groupId);

      if (group.is_income) {
        amount += specificGroupTotals.amount;
        monthly += specificGroupTotals.monthly;
        yearly += specificGroupTotals.yearly;
      } else {
        amount -= specificGroupTotals.amount;
        monthly -= specificGroupTotals.monthly;
        yearly -= specificGroupTotals.yearly;
      }
    }

    return {
      amount,
      monthly,
      yearly,
    };
  }, [groupTotals]);

  return (
    <View
      style={{
        flexDirection: 'row',
        marginRight: 10,
        minHeight: 'unset',
        paddingTop: 10,
      }}
    >
      <View style={{ minWidth: 200 }}>
        <CategoryHeaderCell
          toggleHiddenCategories={toggleHiddenCategories}
          collapseAllCategories={collapseAllCategories}
          expandAllCategories={expandAllCategories}
        />
      </View>
      {viewMode === VIEW_MODE.ONLY_MONTHS ? null : (
        <>
          <View
            style={{ ...headerLabelStyle, textAlign: 'center', width: 120 }}
          >
            <Text style={{ color: theme.pageTextLight }}>Frequency</Text>
          </View>
          <View style={{ ...headerLabelStyle, width: 120 }}>
            <Text style={{ color: theme.pageTextLight }}>Amount</Text>
          </View>
          <View style={{ ...headerLabelStyle, width: 120 }}>
            <View
              style={{
                position: 'absolute',
                top: 0,
                left: 36,
                width: 1,
                height: styles.incomeHeaderHeight,
                backgroundColor: theme.formInputBorder,
              }}
            />
            <Text style={{ color: theme.pageTextLight }}>Monthly</Text>
            <Text style={{ color: theme.pageTextLight, fontWeight: 600 }}>
              {integerToCurrency(totals.monthly)}
            </Text>
          </View>
          <View style={{ ...headerLabelStyle, width: 120 }}>
            <Text style={{ color: theme.pageTextLight }}>Yearly</Text>
            <Text style={{ color: theme.pageTextLight, fontWeight: 600 }}>
              {integerToCurrency(totals.yearly)}
            </Text>
          </View>
          <View
            style={{
              width: '60px',
              minHeight: 30,
            }}
          />
        </>
      )}

      {/*{viewMode === VIEW_MODE.ONLY_BUDGET ? null : (*/}
      <View
        style={{
          flex: 1,
          justifyContent: 'flex-end',
        }}
      >
        <View
          id="table-header-months-scroller"
          style={{
            flexDirection: 'row',
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none' /* for Chrome, Safari and Opera */,
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          <View style={{ ...headerLabelStyle, flex: 1, padding: '0 5px' }}>
            <Text style={{ color: theme.pageTextLight }}>Monthly Avg</Text>
            <Text
              style={{
                color: theme.pageTextLight,
                fontWeight: 600,
              }}
            >
              {format(
                Math.round(visibleTotals.pageYear.savedMonthlyAvg),
                'financial',
              )}
            </Text>
          </View>
          <View style={{ ...headerLabelStyle, flex: 1, padding: '0 5px' }}>
            <Text style={{ color: theme.pageTextLight }}>Yearly</Text>
            <Text style={{ color: theme.pageTextLight, fontWeight: 600 }}>
              {format(visibleTotals.pageYear.saved, 'financial')}
            </Text>
          </View>
          {months.map(o => (
            <View
              key={o.index}
              style={{ ...headerLabelStyle, flex: 1, padding: '0 5px' }}
            >
              <Text style={{ color: theme.pageTextLight }}>{o.month}</Text>
              <Text />
              <Text style={{ color: theme.pageTextLight, fontWeight: 600 }}>
                {format(
                  visibleTotals.pageMonth.get(o.month)?.saved ?? 0,
                  'financial',
                )}
              </Text>
            </View>
          ))}
        </View>
      </View>
      {/*)}*/}
    </View>
  );
}
